import { Text, mergeClasses } from "@fluentui/react-components";
import {
  AddRegular,
  FolderRegular,
  LockClosedRegular,
} from "@fluentui/react-icons";

import { ExistingTeamPicker } from "./ExistingTeamPicker/ExistingTeamPicker";
import { useMicrosoftTeamsTabStyles } from "./MicrosoftTeamsTab.styles";
import { IMicrosoftTeamsTabProps, TeamSource } from "./MicrosoftTeamsTab.types";
import { NewTeamForm } from "./NewTeamForm/NewTeamForm";
import { TeamSummary, getTeamAvatarColor } from "./TeamSummary/TeamSummary";

const TEAM_SOURCE_OPTIONS: {
  source: TeamSource;
  title: string;
  description: string;
  icon: JSX.Element;
}[] = [
    {
      source: "existing",
      title: "Connect to an existing Team",
      description: "Select a Microsoft Team you already have access to",
      icon: <FolderRegular aria-hidden="true" />,
    },
    {
      source: "new",
      title: "Create a new Team",
      description:
        "Create a new Microsoft Team and associated SharePoint site for this Workspace.",
      icon: <AddRegular aria-hidden="true" />,
    },
  ];

const NEW_TEAM_NAME_SEPARATOR = " · ";

export const getDefaultTeamName = (
  workspaceCode: string,
  workspaceName: string,
): string =>
  [workspaceCode, workspaceName]
    .map((part) => part.trim())
    .filter(Boolean)
    .join(NEW_TEAM_NAME_SEPARATOR);

export const MicrosoftTeamsTab = ({
  isConnectedToSite,
  connectedTeam,
  workspaceCode,
  clientName,
  teamMembers,
  teamSource,
  onTeamSourceChange,
  selectedTeamId,
  onSelectedTeamIdChange,
  teamName,
  suggestedTeamName,
  onTeamNameChange,
  onTeamNameReset,
}: IMicrosoftTeamsTabProps): JSX.Element => {
  const styles = useMicrosoftTeamsTabStyles();

  return (
    <div>
      {!isConnectedToSite && <Text as="p" className={styles.description}>
        Connect this Workspace to an existing Microsoft Team, or create a new
        Team for it. The associated SharePoint site will be used to store and
        manage the Workspace content.
      </Text>}
      {!isConnectedToSite &&
        <div
          role="radiogroup"
          aria-label="Team source"
          className={styles.sourceGrid}
        >
          {TEAM_SOURCE_OPTIONS.map(({ source, title, description, icon }) => {
            const isSelected = !isConnectedToSite && teamSource === source;
            return (
              <button
                key={source}
                type="button"
                role="radio"
                aria-checked={isSelected}
                aria-disabled={isConnectedToSite}
                className={mergeClasses(
                  styles.sourceCard,
                  isSelected && styles.sourceCardSelected,
                  isConnectedToSite && styles.sourceCardDisabled,
                )}
                onClick={() => {
                  if (!isConnectedToSite) onTeamSourceChange(source);
                }}
              >
                <span className={styles.sourceHeader}>
                  <span
                    className={mergeClasses(
                      styles.sourceIconTile,
                      isSelected && styles.sourceIconTileSelected,
                    )}
                  >
                    {icon}
                  </span>
                  <span className={styles.sourceTitle}>{title}</span>
                </span>
                <span className={styles.sourceDescription}>{description}</span>
              </button>
            );
          })}
        </div>}

      {isConnectedToSite && (
        <div className={styles.connectedNotice} role="status">
          <div className={styles.connectedNoticeHeader}>
            This Workspace is already connected to
          </div>
          {connectedTeam && (
            <div className={styles.connectedTeamCard}>
              <TeamSummary
                team={connectedTeam}
                avatarColor={getTeamAvatarColor(0)}
              />
            </div>
          )}
        </div>
      )}

      {!isConnectedToSite && teamSource === "existing" && (
        <ExistingTeamPicker
          workspaceCode={workspaceCode}
          clientName={clientName}
          selectedTeamId={selectedTeamId}
          onSelectedTeamIdChange={onSelectedTeamIdChange}
        />
      )}

      {!isConnectedToSite && teamSource === "new" && (
        <NewTeamForm
          teamMembers={teamMembers}
          teamName={teamName}
          suggestedTeamName={suggestedTeamName}
          onTeamNameChange={onTeamNameChange}
          onTeamNameReset={onTeamNameReset}
        />
      )}

      <div className={styles.permanentWarning}>
        <LockClosedRegular
          className={styles.permanentWarningIcon}
          aria-hidden="true"
        />
        <Text className={styles.permanentWarningText}>
          <Text weight="bold">This connection is permanent.</Text> Once you
          connect this Workspace to an existing Microsoft Team or create a new
          Team, the association cannot be changed.
        </Text>
      </div>
    </div>
  );
};